import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Calendar from "react-calendar";
import { format, addDays, isBefore } from "date-fns";
import "react-calendar/dist/Calendar.css";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { fr, enUS } from "date-fns/locale";

const weekdays = [
  { label: { en: "Sun", fr: "Dim" }, value: 0 },
  { label: { en: "Mon", fr: "Lun" }, value: 1 },
  { label: { en: "Tue", fr: "Mar" }, value: 2 },
  { label: { en: "Wed", fr: "Mer" }, value: 3 },
  { label: { en: "Thu", fr: "Jeu" }, value: 4 },
  { label: { en: "Fri", fr: "Ven" }, value: 5 },
  { label: { en: "Sat", fr: "Sam" }, value: 6 },
];

const CoachAvailabilityForm = () => {
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedWeekdays, setSelectedWeekdays] = useState([1]);
  const coachLN = localStorage.getItem("coachLN");
  const { t, i18n } = useTranslation();
  const [clickCount, setClickCount] = useState(0);
  const currentLocale = i18next.language === 'fr' ? fr : enUS;

useEffect(() => {
  const fetchAvailabilities = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000/"}availabilities/coach/${coachLN}`);
      const data = await res.json();
      const dates = data.map(item => item.date);
      setAvailableDates(dates);
    } catch (err) {
      console.error("Failed to fetch availabilities:", err);
    }
  };

  if (coachLN) fetchAvailabilities();
}, [coachLN]);
  
  const toggleWeekday = (value) => {
    setSelectedWeekdays(prev =>
      prev.includes(value)
        ? prev.filter((day) => day !== value)
        : [...prev, value]
    );
  };

  const handleDateClick = (date) => {
  if (clickCount === 0) {
    setDateRange([date, date]);
    setSelectedWeekdays([date.getDay()]);
    setClickCount(1);
  } else {
    const [firstDate] = dateRange;
    if (format(firstDate, "yyyy-MM-dd") === format(date, "yyyy-MM-dd")) {

      setDateRange([date, date]);
      setSelectedWeekdays([date.getDay()]);
    } else {
      const [start, end] = isBefore(date, firstDate)
        ? [date, firstDate]
        : [firstDate, date];
      setDateRange([start, end]);

      let current = new Date(start);
      const daysSet = new Set();
      while (!isBefore(end, current)) {
        daysSet.add(current.getDay());
        current = addDays(current, 1);
      }
      setSelectedWeekdays(Array.from(daysSet));
    }
    setClickCount(0);
  }
};

  const handleSubmit = async () => {
    const [startDate, endDate] = dateRange;
    let current = startDate;
    const dates = [];

    while (!isBefore(endDate, current)) {
      if (selectedWeekdays.includes(current.getDay())) {
        dates.push(format(current, "yyyy-MM-dd"));
      }
      current = addDays(current, 1);
    }

    try {
     const token = localStorage.getItem("token"); 
    await Promise.all(
      dates.map(async (date) => {
      const res = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000/"}availabilities`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify({
        coach: coachLN,
        date,
        start: startTime,
        end: endTime,
      }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      console.error(`❌ Failed to save for ${date}:`, res.status, errorText);
      throw new Error(`Failed for ${date}`);
    }

    return res.json();
  })
);

      alert("✨All availabilities saved successfully");
    } catch (err) {
      console.error("🚨 Error saving one or more availabilities:", err);
      alert("Oops 😬 Something went wrong saving availabilities.");
    }
  };

  const isMultiDay = format(dateRange[0], "yyyy-MM-dd") !== format(dateRange[1], "yyyy-MM-dd");

  return (
    <FormWrapper>
      <SectionTitle>{t("coachAvailabilityForm.selectDateRange")} 🗓️</SectionTitle>
      <StyledCalendar
        onClickDay={handleDateClick}
        value={dateRange}
        selectRange={false}
        locale={i18n.language === 'fr' ? 'fr-FR' : 'en-US'}
        tileContent={({ date, view }) =>
          view === "month" && availableDates.includes(format(date, "yyyy-MM-dd")) ? <Dot /> : null
        }
      />

      {isMultiDay && (
        <>
          <SectionTitle>{t("coachAvailabilityForm.selectWeekdays")}</SectionTitle>
          <WeekdayCheckboxes>
            {weekdays.map((day) => {
              const isChecked = selectedWeekdays.includes(day.value);
              return (
                <CheckboxWrapper key={day.value}>
                  <HiddenCheckbox
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleWeekday(day.value)}
                  />
                  <StyledCheckbox checked={isChecked}>
                    <Checkmark viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </Checkmark>
                  </StyledCheckbox>
                  {day.label[i18n.language === "fr" ? "fr" : "en"]}
                </CheckboxWrapper>
              );
            })}
          </WeekdayCheckboxes>
        </>
      )}

      <SectionTitle>
        {t("coachAvailabilityForm.pickTimesFor", {
          date: `${format(dateRange[0], "PPP", { locale: currentLocale })} - ${format(dateRange[1], "PPP", { locale: currentLocale })}`
        })}
      </SectionTitle>

      <Label htmlFor="startTime">{t("coachAvailabilityForm.startTime")}</Label>
      <Input type="time" id="startTime" value={startTime} onChange={(e) => setStartTime(e.target.value)} />

      <Label htmlFor="endTime">{t("coachAvailabilityForm.endTime")}</Label>
      <Input type="time" id="endTime" value={endTime} onChange={(e) => setEndTime(e.target.value)} />

      <Button onClick={handleSubmit}>{t("coachAvailabilityForm.saveAvailability")}</Button>
    </FormWrapper>
  );
};

export default CoachAvailabilityForm;

const FormWrapper = styled.div`
  background-color: #ffffff;
  padding: 3rem;
  border-radius: 16px;
  max-width: 500px;
  margin: 0 auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h3`
  color: #f28c8c;
  margin-top: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin: 1rem 0 0.5rem;
  color: #6d9886;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 2px solid #f2c8c8;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  background-color: green;
  border-radius: 50%;
  margin: 0 auto;
  margin-top: 2px;
`;

const StyledCalendar = styled(Calendar)`
  margin: 0 auto;
  border: none;
  .react-calendar__tile--active {
    background: rgba(217,121,97,1) !important;
    color: white !important;
  }
  .react-calendar__tile--now {
    background:rgba(148,163,135,1) !important;
  }
`;

const Button = styled.button`
  margin-top: 2rem;
  background-color: rgba(148,163,135,1);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(186,204,171,1);
  }
`;

const WeekdayCheckboxes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0.5rem 0;
  label {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }
  `

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
`;

const HiddenCheckbox = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
`;

const StyledCheckbox = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  background: ${props => props.checked ? '#4caf50' : '#fff'};
  border-radius: 60px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
  border: 1px solid ${props => props.checked ? '#4caf50' : '#ccc'};
  cursor: pointer;
  overflow: hidden;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.35);
  }

  svg {
    stroke-dasharray: 22;
    stroke-dashoffset: ${props => props.checked ? '0' : '22'};
    transition: stroke-dashoffset 0.3s ease;
  }

  &::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    background: rgba(76, 175, 80, 0.3);
    border-radius: 50%;
    transform: scale(0);
    opacity: 0;
    animation: ${props => props.checked ? 'ripple 0.4s ease-out' : 'none'};
    pointer-events: none;
  }

  @keyframes ripple {
    0% {
      transform: scale(0);
      opacity: 0.5;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

const Checkmark = styled.svg`
  width: 16px;
  height: 16px;
  stroke: white;
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
`;